/**
 * SetPassword - Page for VA members to set their password from email link
 */
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import logo from '../assets/logo.svg';
import { setInitialPassword } from '../api/passwordSetup';

interface PasswordValidation {
  minLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumber: boolean;
  passwordsMatch: boolean;
}

export default function SetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const [validation, setValidation] = useState<PasswordValidation>({
    minLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    passwordsMatch: false,
  });

  // Validate password on change
  useEffect(() => {
    setValidation({
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      passwordsMatch: password === confirmPassword && password.length > 0,
    });
  }, [password, confirmPassword]);

  const isPasswordValid =
    validation.minLength &&
    validation.hasUppercase &&
    validation.hasLowercase &&
    validation.hasNumber &&
    validation.passwordsMatch;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !isPasswordValid) return;

    setError('');
    setIsLoading(true);

    try {
      await setInitialPassword(token, password, confirmPassword);
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.detail?.message || 'Failed to set password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="w-screen h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-dark-bg to-gray-900">
        <div className="text-center max-w-md p-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">Invalid Setup Link</h2>
          <p className="text-gray-400 mb-6">This password setup link is invalid or has expired.</p>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-3 bg-accent-blue text-white font-semibold rounded-xl hover:bg-accent-blue/90 transition-colors"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-screen h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-dark-bg to-gray-900">
      <div className="relative w-full max-w-md p-6">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <img src={logo} alt="PARACLETE" className="h-16 w-auto drop-shadow-lg" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">
            {success ? 'Password Created!' : 'Create Your Password'}
          </h1>
          <p className="text-gray-400 text-sm">
            {success ? 'Your password has been successfully created.' : 'Set up your password to access your account.'}
          </p>
        </div>

        {/* Card */}
        <div className="bg-dark-card/50 backdrop-blur-xl border border-dark-border/50 rounded-2xl shadow-2xl overflow-hidden">
          <div className="p-8">
            {success ? (
              <div className="text-center py-4">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                  <svg className="w-8 h-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-gray-400 text-sm mb-6">
                  You can now sign in with your email and password.
                </p>
                <button
                  onClick={() => navigate('/login')}
                  className="w-full py-3 bg-gradient-to-r from-accent-blue to-accent-orange text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-accent-blue/50 transition-all"
                >
                  Sign In
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Password
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={{ fontSize: '16px' }}
                    className="w-full bg-dark-bg/50 border border-dark-border/50 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-accent-blue/50 focus:border-accent-blue transition-all"
                    placeholder="Enter your password"
                    required
                    autoFocus
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    style={{ fontSize: '16px' }}
                    className="w-full bg-dark-bg/50 border border-dark-border/50 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-accent-blue/50 focus:border-accent-blue transition-all"
                    placeholder="Confirm your password"
                    required
                  />
                </div>

                {/* Password Requirements */}
                <div className="bg-dark-bg/30 rounded-xl p-4 space-y-2">
                  <p className="text-sm font-medium text-gray-300 mb-2">Password Requirements:</p>
                  {[
                    { label: 'At least 8 characters', met: validation.minLength },
                    { label: 'One uppercase letter', met: validation.hasUppercase },
                    { label: 'One lowercase letter', met: validation.hasLowercase },
                    { label: 'One number', met: validation.hasNumber },
                    { label: 'Passwords match', met: validation.passwordsMatch },
                  ].map((req, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm">
                      <span className={`${password.length > 0 && req.met ? 'text-green-500' : 'text-gray-500'}`}>
                        {password.length > 0 && req.met ? '✓' : '○'}
                      </span>
                      <span className={`${password.length > 0 && req.met ? 'text-green-400' : 'text-gray-400'}`}>
                        {req.label}
                      </span>
                    </div>
                  ))}
                </div>

                {error && (
                  <div className="bg-red-500/10 border border-red-500/50 rounded-xl p-4 flex items-start gap-3">
                    <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-red-400 text-sm">{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading || !isPasswordValid}
                  className="w-full py-3 bg-gradient-to-r from-accent-blue to-accent-orange text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-accent-blue/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Creating...
                    </span>
                  ) : (
                    'Create Password'
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="w-full py-3 text-gray-400 text-sm hover:text-white transition-colors"
                >
                  Back to Login
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
